import type { AnalyticsProperties, AnalyticsPropertyValue } from "./types.js";

/**
 * Defense-in-depth PII scrubbing for analytics event properties. Callers
 * (the estimator UI) are expected to only ever pass closed-set values
 * (project type ids, province codes, step ids, booleans, counts) - this
 * module exists so that even a future mistake (someone adding
 * `properties: { name: form.name }` to an event call) is caught and
 * stripped before it reaches any sink, rather than relying on every call
 * site getting it right forever. Never throws - a property that fails
 * scrubbing is dropped, not the whole event.
 *
 * Two independent checks, both applied:
 *  1. Key-name denylist - a property whose key looks like it's meant to
 *     carry personal information is dropped outright, regardless of its
 *     value.
 *  2. Value-pattern check - a *string* property whose value looks like an
 *     email address, a phone number, or a Canadian postal code is
 *     dropped, regardless of its key name. This catches PII smuggled
 *     under an innocuous-looking key.
 */

/**
 * Matched case-insensitively against the property key with all
 * non-alphanumeric characters stripped first (so "full_address",
 * "fullAddress", and "full-address" all match "fulladdress").
 */
const DENYLISTED_KEY_SUBSTRINGS = [
  "name",
  "email",
  "phone",
  "address",
  "postalcode",
  "postcode",
  "zipcode",
  "notes",
  "description",
  "comment",
  "message",
  "contact"
];

/**
 * `name` alone would also match legitimate, non-personal keys like
 * `projectType` (no) or `province` (no) - none of the real event
 * properties this package emits need any of these substrings, so the
 * broad match is intentional rather than a bug. If a future event
 * legitimately needs a key containing one of these words, that's a sign
 * the property itself should be reconsidered, not the denylist narrowed.
 */
function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isDenylistedKey(key: string): boolean {
  const normalized = normalizeKey(key);
  return DENYLISTED_KEY_SUBSTRINGS.some((bad) => normalized.includes(bad));
}

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]{2,}/;
/** 7+ consecutive digits (allowing common separators) - catches phone numbers regardless of formatting. */
const PHONE_LIKE_PATTERN = /(?:\d[\s.-]?){7,}/;
const CANADIAN_POSTAL_CODE_PATTERN = /[A-CEGHJ-NPRSTVXY]\d[A-CEGHJ-NPRSTV-Z]\s?\d[A-CEGHJ-NPRSTV-Z]\d/i;

function looksLikePii(value: string): boolean {
  return EMAIL_PATTERN.test(value) || PHONE_LIKE_PATTERN.test(value) || CANADIAN_POSTAL_CODE_PATTERN.test(value);
}

function isSafeValue(value: AnalyticsPropertyValue): boolean {
  if (typeof value !== "string") return true; // numbers/booleans/null can't carry the patterns above
  return !looksLikePii(value);
}

/**
 * Scrubs an analytics properties bag in place (returns a new object -
 * never mutates the input). Any property that fails either check is
 * omitted entirely from the result, not replaced with a redacted
 * placeholder - a present-but-redacted property is still a signal that
 * something was there; omitting it leaves no trace.
 */
export function scrubEventProperties(properties: AnalyticsProperties | undefined): AnalyticsProperties | undefined {
  if (!properties) return undefined;
  const safe: AnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (isDenylistedKey(key)) continue;
    if (!isSafeValue(value)) continue;
    safe[key] = value;
  }
  return Object.keys(safe).length > 0 ? safe : undefined;
}
